import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, addEdge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Play, FloppyDisk, CheckCircle, Broadcast, Plus, Trash, Robot, PlugsConnected, GitBranch, Timer, Lightning, ArrowsClockwise, ClockCounterClockwise, Bug } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NODE_META = {
  trigger: { color: "#0f766e", Icon: Lightning }, action: { color: "#2563eb", Icon: Broadcast },
  condition: { color: "#d97706", Icon: GitBranch }, delay: { color: "#7c3aed", Icon: Timer },
  ai_agent: { color: "#db2777", Icon: Robot }, connector: { color: "#0891b2", Icon: PlugsConnected },
};
const PALETTE = ["action", "condition", "delay", "ai_agent", "connector"];
const STEP_COLOR = { ok: "text-emerald-600", failed: "text-rose-600", awaiting: "text-amber-600", delayed: "text-violet-600", skipped: "text-muted-foreground" };

const toRF = (nodes, edges) => ({
  rfNodes: nodes.map((n) => ({
    id: n.id, position: n.position || { x: 0, y: 0 }, data: { label: `${n.type}: ${n.name || n.id}`, meta: n },
    style: { border: `2px solid ${NODE_META[n.type]?.color || "#888"}`, borderRadius: 6, padding: 6, fontSize: 12, background: "#fff", width: 170 },
  })),
  rfEdges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, label: e.branch, data: { branch: e.branch }, animated: true })),
});

export default function Workflows() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [sel, setSel] = useState(null);
  const [cfgText, setCfgText] = useState("");
  const [nodeName, setNodeName] = useState("");
  const [trigger, setTrigger] = useState({ type: "manual", config: {} });
  const [executions, setExecutions] = useState([]);
  const [debug, setDebug] = useState(null);

  const loadList = () => { api.get("/workflows").then((r) => setList(r.data)).catch(() => {}); api.get("/workflows/templates").then((r) => setTemplates(r.data)).catch(() => {}); };
  useEffect(() => { if (currentOrg) loadList(); }, [currentOrg]);

  const openWf = async (id) => {
    const { data } = await api.get(`/workflows/${id}`);
    setActive(data); setTrigger(data.trigger); setSel(null);
    const { rfNodes, rfEdges } = toRF(data.nodes, data.edges);
    setNodes(rfNodes); setEdges(rfEdges);
    loadExecutions(id);
  };
  const loadExecutions = (id) => api.get(`/workflows/${id}/executions`).then((r) => setExecutions(r.data)).catch(() => {});

  const createBlank = async () => {
    const body = { name: "New workflow", trigger: { type: "manual", config: {} },
      nodes: [{ id: "t1", type: "trigger", name: "Start", config: {}, position: { x: 60, y: 120 } }], edges: [] };
    const { data } = await api.post("/workflows", body); loadList(); openWf(data.id); toast.success(t("wf.created"));
  };
  const fromTemplate = async (tid) => { const { data } = await api.post("/workflows/from-template", { template_id: tid }); loadList(); openWf(data.id); toast.success(t("wf.created")); };

  const onConnect = useCallback((c) => setEdges((eds) => addEdge({ ...c, id: `${c.source}-${c.target}-${Date.now()}`, animated: true }, eds)), [setEdges]);

  const addNode = (type) => {
    const id = `${type[0]}${Date.now().toString().slice(-5)}`;
    const node = { id, position: { x: 200 + Math.random() * 200, y: 80 + Math.random() * 200 },
      data: { label: `${type}: ${type}`, meta: { id, type, name: type, config: {} } },
      style: { border: `2px solid ${NODE_META[type].color}`, borderRadius: 6, padding: 6, fontSize: 12, background: "#fff", width: 170 } };
    setNodes((ns) => [...ns, node]);
  };

  const onNodeClick = (_, node) => {
    setSel(node); const meta = node.data.meta || {};
    setNodeName(meta.name || ""); setCfgText(JSON.stringify(meta.config || {}, null, 2));
  };
  const applyNodeConfig = () => {
    let cfg = {};
    try { cfg = JSON.parse(cfgText || "{}"); } catch { toast.error(t("wf.invalidJson")); return; }
    setNodes((ns) => ns.map((n) => n.id === sel.id ? { ...n, data: { ...n.data, label: `${n.data.meta.type}: ${nodeName}`, meta: { ...n.data.meta, name: nodeName, config: cfg } } } : n));
    toast.success(t("wf.nodeUpdated"));
  };
  const deleteNode = () => { setNodes((ns) => ns.filter((n) => n.id !== sel.id)); setEdges((es) => es.filter((e) => e.source !== sel.id && e.target !== sel.id)); setSel(null); };
  const setEdgeBranch = (branch) => setEdges((es) => es.map((e) => e.id === sel.id ? { ...e, label: branch, data: { branch } } : e));

  const serialize = () => ({
    trigger,
    nodes: nodes.map((n) => ({ id: n.id, type: n.data.meta.type, name: n.data.meta.name, config: n.data.meta.config || {}, position: n.position })),
    edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, branch: e.data?.branch || null })),
  });

  const save = async () => { const b = serialize(); await api.put(`/workflows/${active.id}`, { ...b, name: active.name, description: active.description }); toast.success(t("wf.saved")); openWf(active.id); };
  const validate = async () => { await save(); const { data } = await api.post(`/workflows/${active.id}/validate`); data.valid ? toast.success(`${t("wf.valid")} ${data.warnings.length ? "· " + data.warnings.join("; ") : ""}`) : toast.error(data.errors.join("; ")); };
  const publish = async () => { await save(); try { const { data } = await api.post(`/workflows/${active.id}/publish`); setActive(data); loadList(); toast.success(t("wf.published")); } catch (e) { toast.error(apiError(e)); } };
  const run = async () => { try { const { data } = await api.post(`/workflows/${active.id}/run`, { payload: {} }); toast.success(`${t("wf.runStatus")}: ${data.status}`); setDebug(data); loadExecutions(active.id); } catch (e) { toast.error(apiError(e)); } };
  const tick = async () => { const { data } = await api.post(`/workflows/scheduler/tick`); toast.success(`${t("wf.tick")}: ${data.executed}/${data.due}`); };
  const approve = async (execId) => { try { const { data } = await api.post(`/workflows/executions/${execId}/approve`); toast.success(`${t("wf.resumed")}: ${data.status}`); setDebug(data); loadExecutions(active.id); } catch (e) { toast.error(apiError(e)); } };

  const selMeta = sel && !sel.source ? sel.data?.meta : null;

  return (
    <div data-testid="workflows-page">
      <PageHeader title={t("wf.title")} subtitle={t("wf.subtitle")}
        action={<div className="flex gap-2"><Button variant="outline" className="rounded-sm" onClick={tick} data-testid="scheduler-tick-button"><ArrowsClockwise size={15} className="mr-1" />{t("wf.tick")}</Button><Button className="rounded-sm" onClick={createBlank} data-testid="new-workflow-button"><Plus size={16} className="mr-1" />{t("wf.new")}</Button></div>} />

      <div className="grid grid-cols-[240px_1fr] gap-4">
        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-3" data-testid="workflows-list">
            <div className="overline text-muted-foreground mb-2">{t("wf.workflows")}</div>
            {list.length === 0 && <div className="text-xs text-muted-foreground">{t("wf.none")}</div>}
            {list.map((w) => (
              <button key={w.id} onClick={() => openWf(w.id)} data-testid={`wf-item-${w.id}`}
                className={`w-full text-left px-2 py-1.5 rounded-sm text-sm mb-1 flex items-center justify-between ${active?.id === w.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                <span className="truncate">{w.name}</span>
                <Badge variant={w.status === "active" ? "default" : "secondary"} className="rounded-sm text-[9px] ml-1">{w.status[0]}</Badge>
              </button>
            ))}
          </div>
          <div className="bg-card border border-border rounded-sm p-3" data-testid="templates-list">
            <div className="overline text-muted-foreground mb-2">{t("wf.templates")}</div>
            {templates.map((tp) => (
              <button key={tp.template_id} onClick={() => fromTemplate(tp.template_id)} data-testid={`template-${tp.template_id}`}
                className="w-full text-left px-2 py-1.5 rounded-sm text-xs mb-1 hover:bg-secondary">{tp.name}</button>
            ))}
          </div>
        </div>

        {/* Canvas + panels */}
        {!active ? (
          <div className="bg-card border border-border rounded-sm flex items-center justify-center h-[70vh] text-muted-foreground text-sm">{t("wf.selectHint")}</div>
        ) : (
          <div className="space-y-3">
            <div className="bg-card border border-border rounded-sm p-3 flex flex-wrap items-center gap-2">
              <Input value={active.name} onChange={(e) => setActive({ ...active, name: e.target.value })} className="rounded-sm w-56 h-8" data-testid="wf-name-input" />
              <Badge variant="outline" className="rounded-sm">v{active.version}</Badge>
              <Badge variant={active.status === "active" ? "default" : "secondary"} className="rounded-sm">{active.status}</Badge>
              <div className="flex-1" />
              <span className="text-xs text-muted-foreground mr-1">{t("wf.addNode")}:</span>
              {PALETTE.map((tp) => { const M = NODE_META[tp]; return <Button key={tp} size="sm" variant="outline" className="rounded-sm h-7" onClick={() => addNode(tp)} data-testid={`add-node-${tp}`}><M.Icon size={13} className="mr-1" style={{ color: M.color }} />{tp}</Button>; })}
              <div className="flex-1" />
              <Button size="sm" variant="outline" className="rounded-sm" onClick={save} data-testid="save-workflow-button"><FloppyDisk size={14} className="mr-1" />{t("wf.save")}</Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={validate} data-testid="validate-workflow-button"><CheckCircle size={14} className="mr-1" />{t("wf.validate")}</Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={publish} data-testid="publish-workflow-button"><Broadcast size={14} className="mr-1" />{t("wf.publish")}</Button>
              <Button size="sm" className="rounded-sm" onClick={run} data-testid="run-workflow-button"><Play size={14} className="mr-1" />{t("wf.run")}</Button>
            </div>

            <div className="grid grid-cols-[1fr_280px] gap-3">
              <div className="bg-card border border-border rounded-sm h-[60vh]" data-testid="workflow-canvas">
                <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                  onConnect={onConnect} onNodeClick={onNodeClick} onEdgeClick={(_, e) => setSel(e)} fitView>
                  <Background /><Controls /><MiniMap pannable />
                </ReactFlow>
              </div>

              <div className="space-y-3">
                {/* Trigger config */}
                <div className="bg-card border border-border rounded-sm p-3 space-y-2" data-testid="trigger-panel">
                  <div className="overline text-muted-foreground">{t("wf.trigger")}</div>
                  <Select value={trigger.type} onValueChange={(v) => setTrigger({ type: v, config: {} })}>
                    <SelectTrigger className="rounded-sm h-8" data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manual">manual</SelectItem><SelectItem value="event">event</SelectItem><SelectItem value="schedule">schedule</SelectItem></SelectContent>
                  </Select>
                  {trigger.type === "event" && <Input placeholder="pattern e.g. lead.created" value={trigger.config.pattern || ""} onChange={(e) => setTrigger({ ...trigger, config: { pattern: e.target.value } })} className="rounded-sm h-8 font-mono text-xs" data-testid="trigger-pattern" />}
                  {trigger.type === "schedule" && <Input type="number" placeholder="every_minutes" value={trigger.config.every_minutes || ""} onChange={(e) => setTrigger({ ...trigger, config: { every_minutes: Number(e.target.value) } })} className="rounded-sm h-8 font-mono text-xs" data-testid="trigger-minutes" />}
                </div>

                {/* Node / edge editor */}
                {selMeta && (
                  <div className="bg-card border border-border rounded-sm p-3 space-y-2" data-testid="node-editor">
                    <div className="flex items-center justify-between"><span className="overline text-muted-foreground">{selMeta.type}</span><Button size="sm" variant="ghost" className="text-destructive h-6 w-6 p-0" onClick={deleteNode} data-testid="delete-node-button"><Trash size={13} /></Button></div>
                    <Input value={nodeName} onChange={(e) => setNodeName(e.target.value)} className="rounded-sm h-8" placeholder={t("common.name")} data-testid="node-name-input" />
                    <Label className="text-xs">{t("wf.config")} (JSON)</Label>
                    <Textarea value={cfgText} onChange={(e) => setCfgText(e.target.value)} className="rounded-sm font-mono text-xs min-h-28" data-testid="node-config-input" />
                    <Button size="sm" className="rounded-sm w-full" onClick={applyNodeConfig} data-testid="apply-node-button">{t("wf.applyNode")}</Button>
                  </div>
                )}
                {sel?.source && (
                  <div className="bg-card border border-border rounded-sm p-3 space-y-2" data-testid="edge-editor">
                    <div className="overline text-muted-foreground">{t("wf.edgeBranch")}</div>
                    <Select value={sel.data?.branch || "none"} onValueChange={(v) => setEdgeBranch(v === "none" ? null : v)}>
                      <SelectTrigger className="rounded-sm h-8" data-testid="edge-branch-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">{t("common.none")}</SelectItem><SelectItem value="true">true</SelectItem><SelectItem value="false">false</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Executions + debugger */}
            <div className="grid lg:grid-cols-2 gap-3">
              <div className="bg-card border border-border rounded-sm p-3" data-testid="executions-panel">
                <div className="overline text-muted-foreground mb-2 flex items-center gap-1"><ClockCounterClockwise size={14} />{t("wf.executions")}</div>
                {executions.length === 0 && <div className="text-xs text-muted-foreground">{t("wf.noExec")}</div>}
                {executions.map((ex) => (
                  <div key={ex.id} className="flex items-center justify-between border-b border-border last:border-0 py-1.5" data-testid={`exec-${ex.id}`}>
                    <button className="text-xs font-mono text-left hover:underline" onClick={() => setDebug(ex)}>{ex.id.slice(0, 8)} · {new Date(ex.created_at).toLocaleTimeString()}</button>
                    <div className="flex items-center gap-2">
                      <Badge variant={ex.status === "completed" ? "default" : ex.status === "failed" ? "destructive" : "outline"} className="rounded-sm text-[9px]">{ex.status}</Badge>
                      {ex.status === "awaiting_approval" && <Button size="sm" className="rounded-sm h-6 text-[10px]" onClick={() => approve(ex.id)} data-testid={`approve-${ex.id}`}>{t("wf.approve")}</Button>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-card border border-border rounded-sm p-3" data-testid="debugger-panel">
                <div className="overline text-muted-foreground mb-2 flex items-center gap-1"><Bug size={14} />{t("wf.debugger")}</div>
                {!debug ? <div className="text-xs text-muted-foreground">{t("wf.debugHint")}</div> : (
                  <div className="space-y-1">
                    {debug.steps?.map((s, i) => (
                      <div key={i} className="text-xs font-mono flex items-center gap-2" data-testid={`debug-step-${i}`}>
                        <span className={STEP_COLOR[s.status] || ""}>●</span>
                        <span className="w-20 truncate">{s.type}</span>
                        <span className="text-muted-foreground">{s.status}</span>
                        {s.error && <span className="text-rose-600 truncate">{s.error}</span>}
                      </div>
                    ))}
                    {debug.status === "awaiting_approval" && <div className="text-[11px] text-amber-600 mt-2">⏸ {t("wf.awaiting")}: {debug.awaiting?.node_id}</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
